import { memo, useMemo, Fragment } from 'react';
import { parsePatch } from 'diff';

interface UnifiedDiffViewProps {
  diffStr: string;
  filePath?: string;
}

export const UnifiedDiffView = memo(({ diffStr, filePath }: UnifiedDiffViewProps) => {
  const parsed = useMemo(() => {
    try {
      return parsePatch(diffStr);
    } catch {
      return null;
    }
  }, [diffStr]);

  if (!parsed || parsed.length === 0 || !parsed[0].hunks.length) {
    return (
      <div className="rounded-lg border border-hairline bg-surface overflow-hidden">
        {filePath && (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-hairline">
            <span className="i-ph:file-text text-muted text-[12px]" />
            <span className="font-geist-mono text-[11.5px] text-primary truncate">{filePath}</span>
          </div>
        )}
        <pre className="p-3 max-h-64 overflow-y-auto text-[12px] text-muted font-geist-mono whitespace-pre-wrap">
          {diffStr}
        </pre>
      </div>
    );
  }

  const hunks = parsed[0].hunks;

  // Compute totals across all hunks
  let added = 0;
  let removed = 0;

  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line[0] === '+') {
        added++;
      } else if (line[0] === '-') {
        removed++;
      }
    }
  }

  return (
    <div className="rounded-lg border border-hairline bg-surface overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-hairline">
        <span className="i-ph:file-text text-muted text-[12px] flex-shrink-0" />
        <span className="font-geist-mono text-[11.5px] text-primary truncate">{filePath ?? 'patch'}</span>
        {added > 0 && (
          <span className="ui-pill bg-success/[0.08] text-success border-success/30 !text-[10px]">+{added}</span>
        )}
        {removed > 0 && (
          <span className="ui-pill bg-danger/[0.08] text-danger border-danger/30 !text-[10px]">−{removed}</span>
        )}
      </div>

      <div className="py-2 max-h-64 overflow-y-auto bg-surface">
        {hunks.map((hunk, hi) => (
          <Fragment key={hi}>
            <div className="text-muted px-3.5 pb-1.5 pt-1 font-geist-mono text-[11px]">
              @@ -{hunk.oldStart},{hunk.oldLines} +{hunk.newStart},{hunk.newLines} @@
            </div>
            {hunk.lines.map((line, li) => {
              const prefix = line[0];
              const content = line.slice(1);

              if (prefix === '-') {
                return (
                  <div key={li} className="code-diff-line code-diff-del">
                    <span className="select-none mr-1 opacity-60">-</span>
                    {content}
                  </div>
                );
              }

              if (prefix === '+') {
                return (
                  <div key={li} className="code-diff-line code-diff-add">
                    <span className="select-none mr-1 opacity-60">+</span>
                    {content}
                  </div>
                );
              }

              if (prefix === ' ') {
                return (
                  <div key={li} className="code-diff-line text-secondary">
                    <span className="select-none mr-1 opacity-40"> </span>
                    {content}
                  </div>
                );
              }

              return (
                <div key={li} className="code-diff-line syntax-comment">
                  {line}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
});
